import { Check, Copy } from "lucide-react";
import QRCode from "qrcode";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { ApiError } from "@/api/client";

import { useCashier } from "./CashierProvider";
import { CryptoAssetPicker } from "./payment-methods/CryptoAssetPicker";
import type { CashierCryptoAddress, CashierCryptoAsset } from "./types";

/**
 * Crypto deposit + withdraw panes (donor DepositView crypto pane /
 * WithdrawView crypto tab shapes). The payments crypto API is the record:
 * addresses are get-or-provisioned per (asset, network); withdrawals are
 * hold-first with decimal-string amounts.
 */

function assetKey(asset: CashierCryptoAsset): string {
  return `${asset.symbol}:${asset.network}`;
}

export function CryptoDepositPane() {
  const { adapter, adapterSnapshot } = useCashier();
  const assets = useMemo(
    () =>
      (adapterSnapshot?.crypto?.assets ?? []).filter(
        (asset) => asset.depositEnabled,
      ),
    [adapterSnapshot?.crypto?.assets],
  );
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const selected =
    assets.find((asset) => assetKey(asset) === selectedKey) ?? null;
  const [address, setAddress] = useState<CashierCryptoAddress | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [state, setState] = useState<
    "idle" | "loading" | "provisioning" | "error"
  >("idle");
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const copyTimer = useRef<number | null>(null);

  const loadAddress = useCallback(
    (asset: CashierCryptoAsset) => {
      if (!adapter?.getCryptoDepositAddress) return;
      setState("loading");
      setError(null);
      setAddress(null);
      setQrDataUrl(null);
      adapter
        .getCryptoDepositAddress(
          asset.symbol,
          asset.network,
          new AbortController().signal,
        )
        .then((next) => {
          setAddress(next);
          setState("idle");
        })
        .catch((cause: unknown) => {
          if (cause instanceof ApiError && cause.status === 202) {
            setState("provisioning");
            return;
          }
          setError(
            cause instanceof ApiError
              ? cause.message
              : "Could not load a deposit address. Please try again.",
          );
          setState("error");
        });
    },
    [adapter],
  );

  useEffect(() => {
    if (!address?.address) return;
    let cancelled = false;
    QRCode.toDataURL(address.address, {
      margin: 1,
      width: 168,
      color: { dark: "#000000", light: "#ffffff" },
    })
      .then((url) => {
        if (!cancelled) setQrDataUrl(url);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [address?.address]);

  useEffect(
    () => () => {
      if (copyTimer.current) window.clearTimeout(copyTimer.current);
    },
    [],
  );

  const copyAddress = async () => {
    if (!address?.address) return;
    try {
      await navigator.clipboard.writeText(address.address);
      setCopied(true);
      if (copyTimer.current) window.clearTimeout(copyTimer.current);
      copyTimer.current = window.setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard denied — the address stays selectable text.
    }
  };

  return (
    <div className="cashier-crypto">
      <CryptoAssetPicker
        assets={assets}
        flow="deposit"
        onSelect={(asset) => {
          setSelectedKey(assetKey(asset));
          loadAddress(asset);
        }}
        selected={selected}
      />

      {state === "loading" ? (
        <div className="cashier-crypto__status" role="status">
          <span aria-hidden className="cashier-frame__spinner" />
          <span>Preparing your deposit address…</span>
        </div>
      ) : null}
      {state === "provisioning" ? (
        <div className="cashier-crypto__status" role="status">
          <span aria-hidden className="cashier-frame__spinner" />
          <span>
            Your address is being prepared — try again in a few seconds.
          </span>
          {selected ? (
            <button
              className="cashier-state__retry"
              onClick={() => loadAddress(selected)}
              type="button"
            >
              Check again
            </button>
          ) : null}
        </div>
      ) : null}
      {state === "error" ? (
        <p className="cashier-method-error" role="alert">
          {error}
        </p>
      ) : null}

      {address && selected ? (
        <div className="cashier-crypto__address">
          {qrDataUrl ? (
            <img
              alt={`${address.asset} deposit address QR code`}
              className="cashier-crypto__qr"
              src={qrDataUrl}
            />
          ) : null}
          <div className="cashier-crypto__address-copy">
            <div className="cashier-section-label">
              {address.asset} address ({selected.networkName || address.network}
              )
            </div>
            <code>{address.address}</code>
            {address.memo ? (
              <p className="cashier-crypto__memo">
                Memo (required): <code>{address.memo}</code>
              </p>
            ) : null}
            <button
              className="cashier-crypto__copy"
              onClick={() => void copyAddress()}
              type="button"
            >
              {copied ? (
                <Check aria-hidden size={14} />
              ) : (
                <Copy aria-hidden size={14} />
              )}
              {copied ? "Copied" : "Copy address"}
            </button>
          </div>
          <p className="cashier-crypto__note">
            Send only {address.asset} on{" "}
            {selected.networkName || address.network}
            {selected.minDepositAmount
              ? ` · Min ${selected.minDepositAmount} ${address.asset}`
              : ""}
            {selected.minConfirmations
              ? ` · Credited after ${selected.minConfirmations} confirmation${selected.minConfirmations === 1 ? "" : "s"}`
              : ""}
            . Deposits to the wrong network cannot be recovered.
          </p>
        </div>
      ) : null}
    </div>
  );
}

export function CryptoWithdrawPane() {
  const { adapter, adapterSnapshot, refresh, wallets } = useCashier();
  const assets = useMemo(
    () =>
      (adapterSnapshot?.crypto?.assets ?? []).filter(
        (asset) => asset.withdrawEnabled,
      ),
    [adapterSnapshot?.crypto?.assets],
  );
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const selected =
    assets.find((asset) => assetKey(asset) === selectedKey) ?? null;
  const [addressInput, setAddressInput] = useState("");
  const [memo, setMemo] = useState("");
  const [amount, setAmount] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  // A pam_unavailable retry must reuse the SAME idempotency key.
  const idempotencyKeyRef = useRef<string | null>(null);

  const amountValid = (() => {
    const parsed = Number.parseFloat(amount);
    if (!Number.isFinite(parsed) || parsed <= 0) return false;
    if (selected?.minWithdrawAmount) {
      const min = Number.parseFloat(selected.minWithdrawAmount);
      if (Number.isFinite(min) && parsed < min) return false;
    }
    return true;
  })();

  const submit = async () => {
    if (
      !adapter?.cryptoWithdraw ||
      !selected ||
      !amountValid ||
      !addressInput
    ) {
      return;
    }
    setSubmitting(true);
    setError(null);
    idempotencyKeyRef.current ??= `hw-crypto-ui-${crypto.randomUUID()}`;
    try {
      const result = await adapter.cryptoWithdraw(
        {
          asset: selected.symbol,
          network: selected.network,
          amount,
          address: addressInput.trim(),
          memo: memo.trim(),
          idempotencyKey: idempotencyKeyRef.current,
        },
        new AbortController().signal,
      );
      setDone(
        result.message ??
          "Withdrawal submitted — it will be sent after review.",
      );
      idempotencyKeyRef.current = null;
      setAmount("");
      setAddressInput("");
      setMemo("");
      void refresh();
    } catch (cause) {
      if (cause instanceof ApiError) {
        setError(cause.message);
        // Terminal rejections need a fresh key; retry-safe outages keep it.
        if (cause.code !== "pam_unavailable" && cause.status !== 503) {
          idempotencyKeyRef.current = null;
        }
      } else {
        setError("Something went wrong. Please try again.");
        idempotencyKeyRef.current = null;
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="cashier-state" role="status">
        <strong>Withdrawal Submitted</strong>
        <p>{done}</p>
        <button
          className="cashier-state__retry"
          onClick={() => setDone(null)}
          type="button"
        >
          Done
        </button>
      </div>
    );
  }

  return (
    <div className="cashier-crypto">
      <CryptoAssetPicker
        assets={assets}
        balances={wallets}
        flow="withdraw"
        onSelect={(asset) => {
          setSelectedKey(assetKey(asset));
          setError(null);
        }}
        selected={selected}
        showBalances
      />

      {selected ? (
        <>
          <div className="cashier-section-label">
            {selected.symbol} address (
            {selected.networkName || selected.network})
          </div>
          <input
            aria-label="Withdrawal address"
            autoComplete="off"
            className="cashier-withdraw__amount"
            onChange={(event) => setAddressInput(event.target.value)}
            placeholder={`Paste your ${selected.symbol} address`}
            spellCheck={false}
            type="text"
            value={addressInput}
          />
          <input
            aria-label="Memo (optional)"
            autoComplete="off"
            className="cashier-withdraw__amount"
            onChange={(event) => setMemo(event.target.value)}
            placeholder="Memo / tag (only if your wallet requires it)"
            spellCheck={false}
            type="text"
            value={memo}
          />
          <div className="cashier-withdraw__amount-head">
            <div className="cashier-section-label">
              Amount ({selected.symbol})
            </div>
            <span>
              {selected.minWithdrawAmount
                ? `Min ${selected.minWithdrawAmount}`
                : ""}
              {selected.withdrawalFee
                ? ` · Fee ${selected.withdrawalFee} ${selected.symbol}`
                : ""}
            </span>
          </div>
          <input
            aria-label="Withdrawal amount"
            autoComplete="off"
            className="cashier-withdraw__amount"
            inputMode="decimal"
            onChange={(event) => setAmount(event.target.value)}
            placeholder="0.00"
            step="any"
            type="number"
            value={amount}
          />
          <p className="cashier-withdraw__review-note">
            Withdrawals are reviewed before being sent — this can take a few
            hours during busy periods. Network fees are deducted from the amount
            you receive.
          </p>
          <button
            className="cashier-cta is-confirm"
            disabled={!amountValid || !addressInput.trim() || submitting}
            onClick={() => void submit()}
            type="button"
          >
            {submitting ? "Submitting..." : "Confirm Withdrawal"}
          </button>
        </>
      ) : null}

      {error ? (
        <p className="cashier-method-error" role="alert">
          {error}
        </p>
      ) : null}
    </div>
  );
}
